import { IGameAnimation } from "by/gamefactory/animations/IGameAnimation";
import { gsap, Power4 } from "gsap";
import { Container, IDestroyOptions, Sprite, Texture } from "pixi.js";

export class LoaderAnimation extends Container implements IGameAnimation {
    private readonly duration;
    private readonly maxScale;
    private readonly minScale;
    private readonly startScale;
    private readonly minX;
    private readonly maxX;
    private readonly middleX;
    private readonly ease;
    private readonly timeline: gsap.core.Timeline;
    private readonly textures: Texture[];
    private readonly texturesWidth: number;
    private readonly maxWidth: number;

    public get predictedWidth(): number {
        return this.maxWidth;
    }

    public constructor(textures: Texture[]) {
        super();
        if (textures == undefined || textures.length < 3) {
            throw new Error("Failed to create LoaderAnimation without textures.");
        }
        this.textures = textures;
        this.texturesWidth = this.textures[0].width;
        this.duration = 0.9;
        this.maxScale = 1.5;
        this.minScale = 0.1;
        this.startScale = 0.4;
        this.minX = 0;
        this.maxX = this.texturesWidth * 2;
        this.middleX = this.texturesWidth;
        this.ease = Power4.easeInOut;
        this.maxWidth = this.texturesWidth * (this.maxScale + this.startScale);
        for (let i = 0; i < 3; i++) {
            const sprite = new Sprite(this.textures[i]);
            sprite.anchor.set(0.5);
            this.addChild(sprite);
        }
        const sprite = new Sprite(this.textures[2]);
        sprite.anchor.set(0.5);
        this.addChild(sprite);
        this.timeline = gsap.timeline({ paused: true });
        this.initAnimation();
    }

    private initAnimation(): void {
        const duration = this.duration;
        const maxScale = this.maxScale;
        const minScale = this.minScale;
        const startScale = this.startScale;
        const minX = this.minX;
        const maxX = this.maxX;
        const middleX = this.middleX;
        let j = 0;
        const ease = this.ease;
        const first = this.getChildAt(0) as Sprite;
        const second = this.getChildAt(1) as Sprite;
        const third = this.getChildAt(2) as Sprite;
        const forth = this.getChildAt(3) as Sprite;

        this.timeline.fromTo(first.position, { x: minX }, { x: middleX, duration, ease }, "0");
        this.timeline.fromTo(first.scale, { x: startScale, y: startScale }, { x: maxScale, y: maxScale, duration, ease }, "0");
        this.timeline.fromTo(second.position, { x: middleX }, { x: maxX, duration, ease }, "0");
        this.timeline.fromTo(second.scale, { x: maxScale, y: maxScale }, { x: startScale, y: startScale, duration, ease }, "0");
        this.timeline.set(third.position, { x: maxX, duration, ease }, "0");
        this.timeline.fromTo(third.scale, { x: startScale, y: startScale }, { x: minScale, y: minScale, duration, ease }, "0");
        this.timeline.set(forth.position, { x: minX, duration, ease }, "0");
        this.timeline.fromTo(forth.scale, { x: minScale, y: minScale }, { x: startScale, y: startScale, duration, ease }, "0");
        this.timeline.call(() => {
            first.texture = this.textures[j % 3];
            second.texture = this.textures[(j + 1) % 3];
            third.texture = this.textures[(j + 2) % 3];
            forth.texture = this.textures[(j + 2) % 3];
            j += 2;
        }, null, "<");
    }

    public async play(): Promise<void> {
        await this.timeline.repeat(-1).play();
    }

    public stop(): void {
        this.timeline.pause();
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.timeline?.kill();
        super.destroy(options);
    }
}
